import { useMemo } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
} from '@mui/material';
import type { ConversionBatch, TrangThaiDot } from '../../../../schemas/provinceConversion';

export interface ConversionBatchTableProps {
	batches: ConversionBatch[];
	isLoading: boolean;
	isError: boolean;
	selectedBatchId?: string | null;
	onSelect: (batch: ConversionBatch) => void;
	onRevert: (batch: ConversionBatch) => void;
	onDelete: (batch: ConversionBatch) => void;
}

const STATUS_LABEL: Record<TrangThaiDot, string> = {
	DRAFT: 'Nháp',
	APPLIED: 'Đã áp dụng',
	REVERTED: 'Đã hoàn tác',
};

const STATUS_COLOR: Record<TrangThaiDot, 'default' | 'success' | 'warning'> = {
	DRAFT: 'warning',
	APPLIED: 'success',
	REVERTED: 'default',
};

function formatDate(value: string | null): string {
	if (!value) return '—';
	return new Date(value).toLocaleDateString('vi-VN');
}

/**
 * `Revert` chỉ hiện ở đợt APPLIED **mới nhất**, xác định độc lập với thứ tự
 * mảng đầu vào (dù `list()` phía backend đã sắp `hieuLucTuNam DESC,
 * createdAt DESC`, component tự tính lại để không phụ thuộc giả định đó).
 */
function findLatestAppliedId(batches: ConversionBatch[]): string | null {
	const applied = batches.filter((b) => b.trangThai === 'APPLIED');
	if (applied.length === 0) return null;
	const [latest] = [...applied].sort((a, b) => {
		if (a.hieuLucTuNam !== b.hieuLucTuNam) return b.hieuLucTuNam - a.hieuLucTuNam;
		return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
	});
	return latest.id;
}

export function ConversionBatchTable({
	batches,
	isLoading,
	isError,
	selectedBatchId,
	onSelect,
	onRevert,
	onDelete,
}: ConversionBatchTableProps) {
	const latestAppliedId = useMemo(() => findLatestAppliedId(batches), [batches]);

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress />
			</Box>
		);
	}

	if (isError) {
		return <Alert severity="error">Không thể tải danh sách đợt chuyển đổi.</Alert>;
	}

	if (batches.length === 0) {
		return <Alert severity="info">Chưa có đợt chuyển đổi nào. Nhấn &quot;Tạo đợt mới&quot; để bắt đầu.</Alert>;
	}

	return (
		<TableContainer component={Paper}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Tên</TableCell>
						<TableCell>Năm hiệu lực</TableCell>
						<TableCell>Trạng thái</TableCell>
						<TableCell align="right">Số ánh xạ</TableCell>
						<TableCell>Ngày áp dụng</TableCell>
						<TableCell align="right">Hành động</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{batches.map((batch) => (
						<TableRow key={batch.id} selected={batch.id === selectedBatchId} hover>
							<TableCell>{batch.ten}</TableCell>
							<TableCell>{batch.hieuLucTuNam}</TableCell>
							<TableCell>
								<Chip size="small" label={STATUS_LABEL[batch.trangThai]} color={STATUS_COLOR[batch.trangThai]} />
							</TableCell>
							<TableCell align="right">{batch.soAnhXa}</TableCell>
							<TableCell>{formatDate(batch.appliedAt)}</TableCell>
							<TableCell align="right">
								<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
									<Button size="small" onClick={() => onSelect(batch)}>
										Xem
									</Button>
									{batch.trangThai === 'APPLIED' && batch.id === latestAppliedId && (
										<Button size="small" color="warning" onClick={() => onRevert(batch)}>
											Hoàn tác
										</Button>
									)}
									{batch.trangThai === 'APPLIED' && batch.id !== latestAppliedId && (
										<Tooltip title="Chỉ hoàn tác được đợt APPLIED mới nhất — hãy hoàn tác các đợt sau nó trước.">
											<span>
												<Button size="small" color="warning" disabled>
													Hoàn tác
												</Button>
											</span>
										</Tooltip>
									)}
									{batch.trangThai === 'DRAFT' && (
										<Tooltip title="Xoá đợt nháp">
											<Button size="small" color="error" onClick={() => onDelete(batch)}>
												Xoá
											</Button>
										</Tooltip>
									)}
								</Box>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
